import {Component, Input, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {PaymentsService} from '../../../services/payments.service';
import {ToastrService} from 'ngx-toastr';
import {PropertyService} from '../../../property-module/services/property.service';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ClientLocation} from '../../../clients-module/models/clientLocation';
import {CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {LocationListingType} from '../../drag-drop-location/locationListingType';
import {PaymentGroupModel} from '../../../models/PaymentModels';
import {GroupModel} from '../../../decision-grid/Models/group-model';
import * as _ from 'lodash';

@Component({
  selector: 'app-add-payment-group-modal',
  templateUrl: './add-payment-group-modal.component.html',
  styleUrls: ['./add-payment-group-modal.component.less']
})
export class AddPaymentGroupModalComponent implements OnInit {

  @Input() public clientId: number;
  public form: UntypedFormGroup;
  public locationFilter = new UntypedFormControl('');
  public initialLocations: ClientLocation[] = [];
  public locations: ClientLocation[] = [];
  public availableLocations: ClientLocation[] = [];
  public selectedLocations: ClientLocation[] = [];
  public availableGroups: PaymentGroupModel[] = [];
  public selectedGroup: PaymentGroupModel;
  public step = 0;
  public type = PageType.Modify;

  constructor(
    public readonly modalService: NgbModal,
    private readonly paymentService: PaymentsService,
    private readonly toast: ToastrService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly propertyService: PropertyService,
    public activeModal: NgbActiveModal) {
  }

  public ngOnInit(): void {
    this.createForm();
    this.loadPaymentGroups();
    this.propertyService.propertiesLoaded.subscribe(() => {
      this.locations = this.propertyService.properties;
      this.availableLocations = this.propertyService.properties;
      this.initialLocations = _.cloneDeep(this.propertyService.properties);
    });

    this.propertyService.FetchPropertiesForClient(this.clientId);
    this.registerLocationFilterSubscriber();
  }

  public loadPaymentGroups(): void {
    this.paymentService.loadPaymentGroups(this.clientId).subscribe((r: PaymentGroupModel[]) => {
      this.availableGroups = r;
    });
  }

  public registerLocationFilterSubscriber(): void {
    this.locationFilter.valueChanges.subscribe((name: string) => {

      if (name) {
        const tempList = this.locations.filter((w: ClientLocation) => w.locationName.toLowerCase().indexOf(name) !== -1);
        this.selectedLocations.forEach((r: ClientLocation) => {
          const idx = tempList.findIndex((l: ClientLocation) => l.id === r.id);

          if (idx > -1) {
            tempList.splice(idx, 1);
          }
        });
        this.availableLocations = tempList;

      } else {
        // filter was cleared, so put the available list back to total list minus the selected list
        const tempList = this.locations;
        this.selectedLocations.forEach((r: ClientLocation) => {
          const idx = tempList.findIndex((l: ClientLocation) => l.id === r.id);

          if (idx > -1) {
            tempList.splice(idx, 1);
          }
        });
        this.availableLocations = tempList;
      }
    });
  }

  public createForm(): void {
    this.form = this.formBuilder.group({
      id: [0, Validators.required],
      clientId: [this.clientId, Validators.required],
      groupName: ['', Validators.required]
    });
  }

  public drop(event: CdkDragDrop<string[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex);
      this.ResortArrays();
    }
  }

  public addRemovedClicked(e: LocationListingType, index: number): void {

    if (e === LocationListingType.Add) {
      this.selectedLocations.push(this.availableLocations[index]);
      this.availableLocations.splice(index, 1);
    }

    if (e === LocationListingType.Remove) {
      this.availableLocations.push(this.selectedLocations[index]);
      this.selectedLocations.splice(index, 1);
    }

    this.ResortArrays();
  }

  public ResortArrays(): void {
    this.selectedLocations.sort((a, b) => a.locationName.localeCompare(b.locationName));
    this.availableLocations.sort((a, b) => a.locationName.localeCompare(b.locationName));
  }

  public addAll(): void {

    if (this.availableLocations.length > 0) {
      for (let x = 0; x < this.availableLocations.length; x++) {
        this.selectedLocations.push(this.availableLocations[x]);
      }

      this.availableLocations = [];
      this.ResortArrays();
    }
  }

  public removeAll(): void {
    if (this.selectedLocations.length > 0) {
      for (let x = 0; x < this.selectedLocations.length; x++) {
        this.availableLocations.push(this.selectedLocations[x]);
      }

      this.selectedLocations = [];
      this.ResortArrays();
    }
  }

  public createNewGroup(): void {
    this.type = PageType.Add;
  }

  public cancel(): void {
    this.activeModal.close();
  }

  public selectedGroupChange(e: any) {

    if (this.step === 1) {

      // Ok, so we have already clicked next so we are on the group details.. Reload the data based on this new group
      this.loadSelectedGroup();

    }
  }

  public loadSelectedGroup(): void {
    // clear the lists
    this.selectedLocations = [];

    // this.locations = this.propertyService.properties;
    // this.availableLocations = this.propertyService.properties;
    this.availableLocations = _.cloneDeep(this.initialLocations);

    // Populate the selected list with the group ID's and remove those ids from the available list
    this.form.patchValue(this.selectedGroup);

    this.selectedGroup.selectedLocations.forEach((r: number) => {
      const tt = this.availableLocations.find((e: ClientLocation) => e.id === r);

      if (tt !== undefined) {
        this.selectedLocations.push(tt);
      }

      const idx = this.availableLocations.findIndex((l: ClientLocation) => l.id === r);
      if (idx > -1) {
        this.availableLocations.splice(idx, 1);
      }
    });
  }

  public next(): void {

    if (this.step === 0) {

      if (this.selectedGroup === undefined && this.type === PageType.Modify) {
        this.toast.warning('You must selected a group to modify', 'No Group Selected ', {
          closeButton: true
        });

        return;
      }

      if (this.type === PageType.Modify) {

        this.loadSelectedGroup();

      } else {
        if (!this.form.valid) {
          return;
        }
      }

      // Set the next step
      this.step = 1;

      return;
    }

    if (!this.form.valid) {
      return;
    }
    // Here is where we will save the information
    const selectedIds = this.selectedLocations.map((e: ClientLocation) => e.id);

    const t: GroupModel = this.form.value;
    t.selectedLocations = selectedIds;

    // one call for create/update == save
    this.paymentService.savePaymentGroup(t).subscribe((newGroup: PaymentGroupModel) => {

      this.toast.success('Payment group successfully saved!');

      this.paymentService.loadPaymentGroups(this.clientId).subscribe((r: PaymentGroupModel[]) => {
        this.availableGroups = r;
        const latestGroupAdded = r.find(w => w.groupName.toLocaleLowerCase() === t.groupName.toLocaleLowerCase());

        if (latestGroupAdded) {
          this.paymentService.$lastAddedPaymentGroup.next(latestGroupAdded);
        }
        this.cancel();
      });
    });
  }
}

export enum PageType {
  Modify,
  Add = 1
}
